import { ReactNode, useState } from 'react';
import { ChevronDown, Lock } from 'lucide-react';

import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';

import { Course, sizeLabel } from '../../data/programs';
import { plural } from '../../utils/plural';

interface Props {
  course: Course;
  defaultOpen?: boolean;
  /** Whether paid content is currently openable (a covering subscription). */
  paidUnlocked?: boolean;
  /** Locked-course pill text; Figma 12150:55350 reads "Subscribe to unlock". */
  lockedLabel?: string;
  onSubscribe: () => void;
  /**
   * Whether the header opens. Defaults to "it has modules to list", which is
   * right when the content is already in hand. The interactive tree overrides
   * it, because there a course must be openable BEFORE its curriculum is
   * fetched - opening is what triggers the fetch.
   */
  canOpen?: boolean;
  /** Fires on every toggle, so a lazy body can start loading on first open. */
  onToggle?: (open: boolean) => void;
  /** The expanded body. Each tree supplies its own rows. */
  children?: ReactNode;
}

/**
 * The shell of one course: header, paywall summary, and whatever rows the
 * caller puts inside it.
 *
 * It owns no rows of its own. The informative and interactive trees disagree
 * about what a leaf is - a module with a badge, or an activity with a link -
 * but they render the same header and the same unlock bar, so those live here
 * and the rows arrive as children.
 */
export const CourseAccordion = ({
  course,
  defaultOpen = false,
  paidUnlocked = false,
  lockedLabel = 'Subscribe to unlock',
  onSubscribe,
  canOpen: canOpenProp,
  onToggle,
  children,
}: Props) => {
  // A subscription unlocks even course-level-locked courses.
  const effectiveLocked = course.locked && !paidUnlocked;
  // Being locked does not stop a course opening - seeing what a subscription
  // would buy is the argument for buying one. What stops it is having nothing
  // to list: a course whose content was never fetched, or whose flow came back
  // null because the curriculum is paced.
  const canOpen = canOpenProp ?? course.modules.length > 0;
  const [open, setOpen] = useState(defaultOpen && canOpen);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  /*
   * Counted in ACTIVITIES, not modules, even though the pill's copy says
   * "modules" (product's call). An activity is either free or paid, so the
   * two numbers are exhaustive and neither overstates the other - counting
   * modules could not manage that: a module holding nine free activities and
   * one paid one is `partially-free`, and whichever side it was put on
   * misrepresented it. So the number itself is still an activity count; only
   * the label changed.
   */
  const freeCount = course.freeActivityCount ?? 0;
  const subscriptionCount = course.paidActivityCount ?? 0;
  const showPill = !paidUnlocked && freeCount + subscriptionCount > 0;
  const showUnlockBar = !paidUnlocked && subscriptionCount > 0;
  const size = sizeLabel(course);
  // Present exactly when Savanna resolved the flow for a learner, so it is the
  // enrolled/not-enrolled signal as well as the value.
  const { progress } = course;
  const progressPct = progress
    ? Math.round(
        (progress.completedActivities / progress.totalActivities) * 100
      )
    : 0;

  // Whether there is a second line to show at all - a course with no
  // progress, no split, and nothing locked (the plain informative case with
  // no size either) has only the title row.
  const hasMetaRow = !!progress || showPill || effectiveLocked || !!size;

  return (
    <div className="overflow-hidden rounded-2xl border border-border-secondary bg-card">
      <button
        type="button"
        // Not just unhandled: a header with nothing to open is taken out of
        // the tab order too, so it cannot be focused into a dead end.
        disabled={!canOpen}
        onClick={toggle}
        className={cn(
          'flex w-full flex-col gap-3 p-5 text-left',
          canOpen && 'cursor-pointer'
        )}
      >
        {/*
          Below `xl` this is two rows: title (with the chevron) on top, then
          the completion count/free-paid split/progress bar underneath,
          indented under the title. There is no room to keep all of that on
          one line below `xl`, and the old approach - hiding the second line's
          content below `sm` rather than wrapping it - meant a phone showed no
          progress at all. Wrapping costs a taller card instead of missing
          data.
        */}
        <div
          className={cn(
            'flex w-full flex-col gap-3',
            progress ? '2xl:hidden' : 'xl:hidden'
          )}
        >
          <div className="flex w-full items-center gap-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-sm font-medium text-heading">
              {course.index}
            </span>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium text-heading">{course.title}</span>
              {/*
                A bordered pill rather than loose text, as Figma 12150:55360
                draws it on courses 2-6.
              */}
              {effectiveLocked && (
                <span className="shrink-0 rounded-full border border-saffron/30 bg-saffron/10 px-2.5 py-0.5 text-xs font-medium text-saffron-active">
                  {lockedLabel}
                </span>
              )}
            </div>

            {/*
              Absent, not dimmed, when there is nothing to open: a faded
              chevron still reads as "expandable but disabled", and these
              courses are not going to open at all - their content is
              deliberately not fetched.
            */}
            {canOpen && (
              <ChevronDown
                className={cn(
                  'h-5 w-5 shrink-0 text-muted-foreground transition-transform',
                  open && 'rotate-180'
                )}
              />
            )}
          </div>

          {hasMetaRow && (
            <div className="flex flex-col gap-2 pl-[52px]">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                {progress ? (
                  <span className="shrink-0 text-sm text-muted-foreground">
                    {progress.completedActivities} of{' '}
                    {progress.totalActivities} completed modules
                  </span>
                ) : (
                  // Only reached without progress: enrolled courses already
                  // state the count as "X of Y completed modules", and
                  // repeating it as its own "Y modules" chip would say the
                  // same number twice.
                  size && (
                    <span className="shrink-0 text-sm text-muted-foreground">
                      {size}
                    </span>
                  )
                )}

                {effectiveLocked && (
                  <Lock className="h-5 w-5 shrink-0 text-muted-foreground" />
                )}
                {!effectiveLocked && showPill && (
                  <span className="shrink-0 border border-border-secondary px-3 py-1 text-xs font-medium">
                    <span className="text-primary">
                      {freeCount} free {plural(freeCount, 'module', 'modules')}
                    </span>
                    <span className="text-saffron-active">
                      {' '}
                      · {subscriptionCount} in Subscription
                    </span>
                  </span>
                )}
              </div>

              {progress && (
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/*
          `xl` and up: the sidebar has claimed its width, and the content column
          fits the title, count, split and chevron on the one line Figma draws.
          With progress there is a bar as well, and between `xl` and `2xl` the
          column is too narrow for it, which clipped the split pill
          (PEDS-17221). So a course with progress keeps the stacked layout
          until `2xl`, where the bar can shrink to fit.
        */}
        <div
          className={cn(
            'hidden w-full items-center gap-4',
            progress ? '2xl:flex' : 'xl:flex'
          )}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-sm font-medium text-heading">
            {course.index}
          </span>

          <div className="flex min-w-[160px] flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium text-heading">{course.title}</span>
            {effectiveLocked && (
              <span className="shrink-0 rounded-full border border-saffron/30 bg-saffron/10 px-2.5 py-0.5 text-xs font-medium text-saffron-active">
                {lockedLabel}
              </span>
            )}
          </div>

          {hasMetaRow && (
            // Shrinkable, so the progress bar inside can give up width before
            // the count or the pill are clipped.
            <div className="flex min-w-0 items-center gap-3">
              {progress ? (
                <>
                  <span className="shrink-0 text-sm text-muted-foreground">
                    {progress.completedActivities} of{' '}
                    {progress.totalActivities} completed modules
                  </span>
                  <div className="h-2 w-40 min-w-16 shrink overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </>
              ) : (
                size && (
                  <span className="shrink-0 text-sm text-muted-foreground">
                    {size}
                  </span>
                )
              )}

              {effectiveLocked && (
                <Lock className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}
              {!effectiveLocked && showPill && (
                <span className="shrink-0 border border-border-secondary px-3 py-1 text-xs font-medium">
                  <span className="text-primary">
                    {freeCount} free {plural(freeCount, 'module', 'modules')}
                  </span>
                  <span className="text-saffron-active">
                    {' '}
                    · {subscriptionCount} in Subscription
                  </span>
                </span>
              )}
            </div>
          )}

          {canOpen && (
            <ChevronDown
              className={cn(
                'h-5 w-5 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-180'
              )}
            />
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-border-secondary px-6 py-5">
          {children}

          {showUnlockBar && (
            <div className="mt-5 flex flex-col gap-3 rounded-xl border border-saffron bg-saffron/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              {/*
                Program-agnostic on purpose, and that is what Figma reads. A
                plan covers a set of programs, so "the remaining N modules in
                this course" described the smallest thing the money buys.
              */}
              <div>
                <p className="font-medium text-heading">Unlock All-Access</p>
                <p className="text-sm text-muted-foreground">
                  Upgrade to access all programs
                </p>
              </div>
              <Button
                onClick={onSubscribe}
                className="shrink-0 rounded-full bg-saffron px-6 text-foreground hover:bg-saffron-active"
              >
                Subscribe to All Access
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
